import { ReactNode, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { DentalFlowLogo } from "@/components/DentalFlowLogo";
import { toast } from "sonner";
import { PasswordResetDialog } from "@/components/PasswordResetDialog";
import { DoctorComplianceDialog } from "@/components/DoctorComplianceDialog";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useAuth } from "@/contexts/AuthContext";
import { BottomNav } from "@/components/BottomNav";
import { OnlineStatusIndicator } from "@/components/OnlineStatusIndicator";
import { InstallPrompt } from "@/components/InstallPrompt";
import { SyncStatusIndicator } from "@/components/SyncStatusIndicator";
import { useProfile } from "@/hooks/useProfile";
import { ToothLoader } from "@/components/ToothLoader";
import { AppFooter } from "@/components/AppFooter";
import { NotificationDropdown } from "@/components/notifications/NotificationDropdown";
import { ThemeToggle } from "@/components/ThemeToggle";
import { GlobalSearchButton } from "@/components/header/GlobalSearchButton";
import { GlobalSearchDialog } from "@/components/header/GlobalSearchDialog";
import { AnnouncementRenderer } from "@/components/announcements/AnnouncementRenderer";
import { SubscriptionGraceBanner } from "@/components/SubscriptionGraceBanner";
import { useStandardRole } from "@/hooks/useStandardRole";
import { useStaffOnboarding, computeOnboardingStatus } from "@/hooks/useStaffOnboarding";

interface LayoutProps {
  children: ReactNode;
}

const COMPLIANCE_DISMISS_KEY = "compliance_dismiss_count";

const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { data: profile } = useProfile();
  const { data: standardRole } = useStandardRole();
  const { data: checklist } = useStaffOnboarding(user?.id);
  const [searchOpen, setSearchOpen] = useState(false);

  const [dismissCount, setDismissCount] = useState(() =>
    Number(sessionStorage.getItem(COMPLIANCE_DISMISS_KEY) || 0)
  );
  const [showComplianceDialog, setShowComplianceDialog] = useState(false);
  const [hasShownThisNavigation, setHasShownThisNavigation] = useState(false);

  // Use standard role to determine if user is a doctor (for compliance dialog)
  const isDoctorRole = standardRole?.isDoctor || false;
  const onboardingStatus = computeOnboardingStatus(checklist || []);
  const hasIncompleteSteps = onboardingStatus.incomplete.length > 0;
  const passwordResetRequired = profile?.password_reset_required || false;
  const isOnProfilePage = location.pathname.startsWith("/profile");
  const isBlocked = dismissCount >= 3;

  // Show dialog only on initial load or when blocked, not on every route change
  useEffect(() => {
    if (
      isDoctorRole &&
      hasIncompleteSteps &&
      !passwordResetRequired &&
      !isOnProfilePage
    ) {
      if (isBlocked) {
        // When blocked, always show the dialog (user must complete profile)
        setShowComplianceDialog(true);
      } else if (!hasShownThisNavigation) {
        // Only show once per navigation, not repeatedly
        setShowComplianceDialog(true);
        setHasShownThisNavigation(true);
      }
    } else {
      setShowComplianceDialog(false);
    }
  }, [isDoctorRole, hasIncompleteSteps, passwordResetRequired, isOnProfilePage, isBlocked]);

  // Reset the "shown this navigation" flag when route changes
  useEffect(() => {
    setHasShownThisNavigation(false);
  }, [location.pathname]);

  const handleComplianceDismiss = useCallback(() => {
    const newCount = dismissCount + 1;
    setDismissCount(newCount);
    sessionStorage.setItem(COMPLIANCE_DISMISS_KEY, String(newCount));
    setShowComplianceDialog(false);
    setHasShownThisNavigation(true);
  }, [dismissCount]);

  const handleSignOut = async () => {
    sessionStorage.removeItem(COMPLIANCE_DISMISS_KEY);
    const { clearTenantCache } = await import("@/utils/resolveUserTenant");
    clearTenantCache();
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Error signing out");
    } else {
      toast.success("Signed out successfully");
      navigate("/auth");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <DentalFlowLogo size="lg" className="mx-auto" />
          <ToothLoader size="md" />
        </div>
      </div>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <OnlineStatusIndicator />
        <InstallPrompt />
        <AnnouncementRenderer />
        <PasswordResetDialog
          open={passwordResetRequired}
          userId={user?.id || ""}
        />

        {/* Doctor compliance dialog - shown after password reset is handled */}
        {!passwordResetRequired && (
          <DoctorComplianceDialog
            open={showComplianceDialog}
            incompleteSteps={onboardingStatus.incomplete}
            dismissCount={dismissCount}
            onDismiss={handleComplianceDismiss}
          />
        )}

        <AppSidebar profile={profile} onSignOut={handleSignOut} />

        {/* min-w-0: without it, a wide table's intrinsic width propagates up and
            blows this column (top bar included) past the viewport — window-level
            horizontal scroll that slides content under the fixed sidebar. */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile Header with Navigation Trigger */}
          <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 lg:hidden">
            <div className="flex h-14 items-center px-4 gap-3">
              <SidebarTrigger className="h-9 w-9 flex-shrink-0" />
              <div className="flex-1 flex justify-center md:hidden">
                <DentalFlowLogo size="md" />
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <GlobalSearchButton onClick={() => setSearchOpen(true)} />
                <ThemeToggle />
                <NotificationDropdown />
                <SyncStatusIndicator />
              </div>
              <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
            </div>
          </header>

          {/* Desktop Header */}
          <header className="hidden lg:flex sticky top-0 z-50 w-full h-14 items-center justify-between px-6 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center">
              <GlobalSearchButton onClick={() => setSearchOpen(true)} />
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <NotificationDropdown />
              <SyncStatusIndicator />
            </div>
          </header>

          <main
            className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden has-[[data-clinical-case-sheet]]:overflow-visible flex flex-col"
            style={{
              paddingBottom: 'calc(6rem + env(safe-area-inset-bottom, 0px))',
              paddingTop: '1rem'
            }}
          >
            <SubscriptionGraceBanner />
            <div className="px-4 md:px-6 flex-1">
              {children}
            </div>
            <div className="px-4 py-4 mt-auto">
              <AppFooter />
            </div>
          </main>
        </div>
        <BottomNav />
      </div>
    </SidebarProvider>
  );
};

export default Layout;
